import { defineComponent as _defineComponent } from 'vue'
import { InvoicesFormFields, RenderedForm } from '@/components/billing/InvoicesTab/types/InvoicesTabComponentTypes'
import {
  ActionName as GridActionName,
  ClickEventArgs,
  CommandClickEventArgs,
  ItemModel,
  CommandModel,
  commandButtonType
} from '@/components/grids/LfGrid/LfGridTypes'
import { Icons } from '@/icons/icons'
import Vue, { computed, ComputedRef, onMounted, ref, watchEffect } from 'vue'
import SpinnerLayerComponent from '@/components/Spinner/SpinnerLayerComponent.vue'

import { useVuetify } from '@/composables/useVuetify'
import { ListNames } from '@/store/modules/configuration/configurationTypes'
import usePartialbankdraftForm from '@/components/billing/BankdraftTab/composables/usePartialbankdraftForm'
import useBankdraftForm from '@/components/billing/BankdraftTab/composables/useBankdraftForm'
import useCustomerInvoiceBankdraft from '@/components/billing/BankdraftTab/composables/useCustomerInvoiceBankdraft'
import useBankdraftGeneratorForm from '@/components/billing/BankdraftTab/composables/useBankdraftGeneratorForm'
import useGridConfiguration from '@/composables/useGridConfiguration'
import usePermissions from '@/composables/usePermissions'
import useDialog from '@/composables/useDialog'
import useFormData from '@/composables/useFormData'
import FormGeneratorComponent from '@/components/forms/FormGenerator/FormGeneratorComponent.vue'
import LfGridComponent from '@/components/grids/LfGrid/LfGridComponent.vue'
import PromptDialogComponent from '@/components/Dialog/PromptDialogComponent.vue'
import { useI18n } from 'vue-i18n-composable'
import { ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import { ContextMenuClickEventArgs, RecordClickEventArgs, RowDataBoundEventArgs } from '@syncfusion/ej2-vue-grids'
import LfTotalsComponent from '@/components/LfTotals/LfTotalsComponent.vue'
import InvoicesMaxNumberFormFieldsLogicMixin from '@/mixins/InvoicesMaxNumberFormFieldsLogic/InvoicesMaxNumberFormFieldsLogicMixin.vue'
import { TaxesFormFields } from '@/mixins/TaxesDynamicFormFields/types/TaxesDynamicFormFieldsMixinTypes'
import BillingMixin from '@/mixins/BillingMixin.vue'
import { Partialbankdraft } from '@/store/modules/invoices/invoicesTypes'
import { toggleFieldEnable } from '@/helpers/form'
import { propertyExist } from '@/helpers/object'
import StaticAlertComponent from '@/components/Alert/StaticAlertComponent.vue'
import { AlertsData, AlertsTypes } from '@/store/modules/alerts/alertsTypes'
import { BankdataIdEnum } from '@/store/modules/facturaE/facturaETypes'

// TRANSLATIONS

export default /*#__PURE__*/_defineComponent({
  __name: 'BankdraftTabComponent',
  emits: ['isValidForm'],
  setup(__props, { emit }) {


const { t } = useI18n()
const texts = {
  buttons: {
    add: t('action_buttons.add').toString(),
    close: t('action_buttons.close').toString(),
    remove: t('action_buttons.remove').toString(),
    save: t('action_buttons.save').toString(),
    print: t('action_buttons.print').toString(),
    export: t('action_buttons.export').toString(),
    cancel: t('action_buttons.cancel').toString()
  },
  dialogRemoveBankdraft: t('components.customer_invoices_bankdrafts.dialog_remove_detail').toString(),
  dialogRemovePartialbankdraft: t('components.customer_invoices_partialbankdrafts.dialog_remove_detail').toString(),
  contextMenuItems: {
    edit: t('components.context_menu.edit').toString(),
    addPartialbankdraft: t('components.context_menu.add_partialbankdraft').toString(),
    remove: t('components.context_menu.remove').toString()
  },
  promptTitles: {
    newBankdraft: 'Nuevo Cobro',
    editBankdraft: 'Editar Cobro',
    editPartialbankdraft: t('components.customer_invoices_partialbankdrafts.edit_detail').toString(),
    newPartialbankdraft: t('components.customer_invoices_partialbankdrafts.new_detail').toString()
  },
  gridTitle: t('components.customer_invoices_partialbankdrafts.grid_title').toString(),
  noResultsSecondParagraph: t('components.no_results.no_results_second_paragraph').toString(),
  charged: t('components.file_manager.grid_columns.public_ok').toString(),
  info_paid_method: `${t('components.alerts.bankdata_missing').toString()}`
}

// DATA
const loading = ref(true)
const selectedInvoiceCompanyInfoCloned = ref()
const bankdraftId = ref()
const bankdraftFormData = ref()
const partialbankdraftFormData = ref()
const bankdraftPromptOpened = ref(false)
const partialbankdraftPromptOpened = ref(false)
const bankdraftPromptTitle = ref(texts.promptTitles.editBankdraft)
const partialbankdraftPromptTitle = ref(texts.promptTitles.editPartialbankdraft)
const invoicesMaxNumberFormFieldsLogicMixin = new InvoicesMaxNumberFormFieldsLogicMixin()
const billingMixin = new BillingMixin()

// COMPOSABLES
const { prepareFormData, registerFormData, isValidFormData } = useFormData()
const { getDialogInfo, getDialogWarning } = useDialog()
const { breakpointName } = useVuetify()
const {
  customerInvoiceId,
  invoiceCode,
  totals,
  bankdraftList,
  existsEndpointError,
  customerInvoice,
  removeCustomerInvoiceBankdraft,
  removePartialbankdraft,
  saveNewPartialbankdraft,
  saveExistPartialbankdraft,
  saveCustomerInvoice,
  automaticInsert,
  loadCustomerInvoice,
  loadTotals,
  loadBankdraftList,
  getBankdraft,
  clearInvoiceCharged
} = useCustomerInvoiceBankdraft()

const { generatorFormContext, generatorFormSchema, loadGeneratorFormSchema } = useBankdraftGeneratorForm()
const {
  bankdraftFormContext,
  bankdraftFormSchema,
  saveBankdraftForm,
  loadBankdraftFormSchema,
  isPaymentTypeWithBankdata,
  isDirectDebitWithoutBankdata,
  loadBankdata,
  bankdata,
  setPaymentTypeId,
  validateSageExported
} = useBankdraftForm()
const { partialbankdraftFormContext, partialbankdraftFormSchema, loadPartialbankdraftFormSchema } =
  usePartialbankdraftForm()
const { viewPermission, permissionsEntity } = usePermissions()
const { canSave, canDelete } = viewPermission.value
const { gridConfiguration } = useGridConfiguration({
  context: ListNames.CUSTOMER_BANKDRAFTS_DETAILS,
  listName: ListNames.CUSTOMER_BANKDRAFTS_DETAILS,
  commandButtons:
    !canSave && !canDelete
      ? []
      : [
          ...(canSave
            ? [
                {
                  id: GridActionName.PARTIALBANKDRAFT_ADD,
                  type: commandButtonType.NONE,
                  title: texts.contextMenuItems.addPartialbankdraft,
                  buttonOption: {
                    iconCss: Icons.HAND_SPHERE,
                    cssClass: 'custombutton'
                  }
                }
              ]
            : ([] as CommandModel[])),
          ...(canDelete
            ? [
                {
                  id: GridActionName.REMOVE,
                  type: commandButtonType.NONE,
                  title: texts.buttons.remove,
                  buttonOption: {
                    iconCss: Icons.REMOVE,
                    cssClass: 'custombutton'
                  }
                }
              ]
            : ([] as CommandModel[]))
        ]
})

const getPartialBankdraftList = computed(() =>
  bankdraftList.value.reduce((acc: any[], item: { partialBankdraft: string | any[] }) => {
    if (item.partialBankdraft && item.partialBankdraft.length > 0) {
      acc.push(...item.partialBankdraft)
    }
    return acc
  }, [])
)

const miniGrid = ref({
  dataSource: getPartialBankdraftList,
  queryString: 'bankdraftId',
  allowSorting: true,
  rowHeight: '40',
  columns: [
    { field: 'chargeDate', headerText: 'Fecha de cobro', width: 30, allowSorting: true },
    { field: 'amount', headerText: 'Importe', width: 30, allowSorting: true },
    { field: 'notes', headerText: 'Nota', width: 200, allowSorting: true },
    {
      width: 60,
      headerText: '',
      allowResizing: false,
      allowEditing: false,
      allowSearching: false,
      allowReordering: false,
      textAlign: 'Left',
      customAttributes: {
        class: 'commands-columns'
      },
      showInColumnChooser: false,
      commands: [
        {
          id: GridActionName.REMOVE,
          type: 'None',
          title: texts.contextMenuItems.remove,
          buttonOption: {
            iconCss: 'lf-icon-trash',
            cssClass: 'custombutton'
          }
        }
      ]
    }
  ],
  contextMenuItems: [
    {
      id: ActionName.EDIT,
      text: texts.contextMenuItems.edit,
      iconCss: Icons.EDIT
    },
    {
      id: ActionName.REMOVE,
      text: texts.contextMenuItems.remove,
      iconCss: Icons.REMOVE
    }
  ],
  contextMenuClick: (args: any) => {
    const { item, rowInfo } = args
    switch (item.id) {
      case ActionName.EDIT:
        showPartialbankdraftEditForm(rowInfo.rowData)
        break
      case ActionName.REMOVE:
        showRemovePartialbankdraftDialog(rowInfo.rowData as Partialbankdraft)
        break
    }
  },
  commandClick: (args: any) => {
    const { rowData } = args
    showRemovePartialbankdraftDialog(rowData as Partialbankdraft)
  },
  recordClick: (args: any) => {
    const { rowData } = args
    showPartialbankdraftEditForm(rowData)
  }
})

const onRowDataBound = ({ row, data }: RowDataBoundEventArgs) => {
  const { partialBankdraft } = data as any
  // remove caret when no partial bankdraft
  if (partialBankdraft.length === 0) {
    const rowElement = row
    const cellElement = rowElement!.querySelector('td')
    cellElement!.innerHTML = ' '
    cellElement!.className = 'e-customizedexpandcell'
  }
}

// COMPUTED
const breakpointClass: ComputedRef<string> = computed(() => String(breakpointName))
const toolbarOptionsCustom = computed((): ItemModel[] => {
  const { canSave } = viewPermission.value
  return [
    {
      id: GridActionName.TITLE,
      text: texts.gridTitle,
      cssClass: 'lf-title',
      align: 'Left'
    },
    {
      id: GridActionName.ADD,
      text: texts.buttons.add,
      align: 'Right',
      tooltipText: texts.buttons.add,
      cssClass: 'lf-btn-model4',
      disabled: !isValidFormData(RenderedForm.CUSTOMER_BANKDRAFTS_FORM) || !canSave
    },
    {
      id: GridActionName.CUSTOM_TOOLBAR_CONTEXT_MENU,
      align: 'Right',
      cssClass: 'lf-btn-model1',
      prefixIcon: Icons.KEBAB,
      contextMenuItems: [
        {
          id: GridActionName.PRINT,
          iconCss: Icons.PRINT,
          text: texts.buttons.print
        },
        {
          id: GridActionName.EXPORT,
          iconCss: Icons.DOWNLOAD,
          text: texts.buttons.export
        }
      ]
    }
  ]
})

const contextMenuItems = computed(() => {
  const { canSave, canDelete } = viewPermission.value

  return [
    ...(canSave
      ? [
          {
            id: ActionName.EDIT,
            text: texts.contextMenuItems.edit,
            iconCss: Icons.EDIT
          },
          {
            id: ActionName.ADD,
            text: texts.contextMenuItems.addPartialbankdraft,
            iconCss: Icons.HAND_SPHERE
          }
        ]
      : []),
    ...(canDelete
      ? [
          {
            id: ActionName.REMOVE,
            text: texts.contextMenuItems.remove,
            iconCss: Icons.REMOVE
          }
        ]
      : [])
  ]
})
const alert = computed((): AlertsData | false => {
  return propertyExist(registerFormData.value, 'B2BpaymentMetod') && isDirectDebitWithoutBankdata.value
    ? {
        type: AlertsTypes.INFO,
        message: texts.info_paid_method
      }
    : false
})
// EVENTS
function onIsValidForm(isValid: boolean) {
  emit('isValidForm', isValid)
}

function onAutomaticBankdraft() {
  if (bankdraftList.value.length > 0) {
    getDialogInfo({
      message: t('components.customer_invoices_bankdrafts.new_automatic_bankdraft', {
        invoiceName: invoiceCode
      }).toString(),
      action: generateAutomaticBankdraft,
      mainButtonText: texts.buttons.save,
      secondaryButtonText: texts.buttons.cancel
    })
  } else {
    generateAutomaticBankdraft()
  }
}
async function onChangeGeneratorForm(schema: [], formData: any, field: any, value: any) {
  // paymentType change
  if (field.id === InvoicesFormFields.PAYMENT_TYPE) {
    Vue.set(formData, InvoicesFormFields.EXPIRATION_DAY_BETWEEN, value ? value.daysBetweenDueDate : 0)
    Vue.set(formData, InvoicesFormFields.EXPIRATION_DAY_FIRST, value ? value.daysFirstDueDate : 0)
    Vue.set(formData, InvoicesFormFields.EXPIRATION_NUMBER, value ? value.numDueDate : 0)
    Vue.set(formData, InvoicesFormFields.EXPIRATION_TEXT, value ? value.text : '')

    // Bankdata logic
    setPaymentTypeId(value.paymentType)
    await loadBankdata()
    const bankdataField = schema.find((item: any) => item.id === InvoicesFormFields.BANKDATA)
    const bankdataFieldEnabled = enablebankdataField(value.paymentType)
    if (bankdataField) {
      ;(bankdataField as any).propItems = bankdata.value
      Vue.set(formData, InvoicesFormFields.BANKDATA, null)
    }
    toggleFieldEnable(formData, schema, InvoicesFormFields.BANKDATA, bankdataFieldEnabled)
  }
  await prepareFormData(formData) // @note: cuando cambia se actualiza registerFormData (ver watchEffect)
  enableOrDisableGeneratorButton()
}

function enablebankdataField(paymentType: string | null = '') {
  const { idProfessionalFacturaE } = registerFormData.value
  const isValidPaymentType = [BankdataIdEnum.DIRECT_DEBIT_ID, BankdataIdEnum.TRANSFER_ID].includes(
    String(paymentType ?? '')
  )
  return idProfessionalFacturaE === 'active' && isValidPaymentType
}

async function enableOrDisableGeneratorButton() {
  if (!generatorFormSchema.value || !Array.isArray(generatorFormSchema.value)) {
    return
  }

  const formButton = generatorFormSchema.value.find((item: any) => {
    return item.id === InvoicesFormFields.FORM_BUTTON
  })

  if (!formButton) {
    return
  }

  const formValid = await isValidFormData(RenderedForm.CUSTOMER_BANKDRAFTS_FORM)
  const { canSave } = viewPermission.value

  const hasExpirations = registerFormData.value && registerFormData.value[InvoicesFormFields.EXPIRATION_NUMBER] > 0

  formButton.disabled = !(canSave && hasExpirations && formValid)
}

async function onChangeBankdraftForm(schema: [], formData: any, field: any, value: any) {
  // Charged date field logic when payment type provission selected - Billing mixin
  if (field.id === TaxesFormFields.CHARGED) {
    billingMixin.chargedSwitchLogic(schema, formData, field, value)
  }
}

function resetBankdraftFormSchema() {
  const chargeDateInput = bankdraftFormSchema.value.find((item: any) => {
    return item.id === InvoicesFormFields.CHARGE_DATE
  }) as any
  chargeDateInput.disabled = true
}

async function onBankdraftPromptExecuteAction(actionName: string) {
  if (ActionName.SAVE === actionName) {
    await saveBankdraftForm(bankdraftFormData.value, customerInvoiceId)
    if (!existsEndpointError.value) {
      await reloadData()
    }
  }
  resetBankdraftFormSchema()
  bankdraftPromptOpened.value = false
}

async function onPartialbankdraftPromptExecuteAction(actionName: string) {
  if (ActionName.SAVE === actionName) {
    await savePartialbankdraft()

    if (!existsEndpointError.value) {
      await reloadData()
    }
  }
  partialbankdraftPromptOpened.value = false
}

function onCommandClick({ commandColumn, rowData }: CommandClickEventArgs) {
  switch (commandColumn.id) {
    case GridActionName.PARTIALBANKDRAFT_ADD:
      showPartialbankdraftCreateForm(rowData)
      break
    case GridActionName.REMOVE:
      showRemoveBankdraftDialog((rowData as any).id, (rowData as any).description)
      break
  }
}

function onToolbarClicked({ item }: ClickEventArgs) {
  if (ActionName.ADD === item.id) {
    showBankdradftCreateForm()
  }
}

function onRowClicked({ rowData }: RecordClickEventArgs) {
  const canSave = viewPermission.value.canSave
  if (!canSave) {
    return
  }
  showBankdradftEditForm(rowData)
}

function onContextMenuClicked({ item, rowInfo }: ContextMenuClickEventArgs) {
  switch (item.id) {
    case ActionName.EDIT:
      showBankdradftEditForm(rowInfo?.rowData)
      break
    case ActionName.ADD:
      showPartialbankdraftCreateForm(rowInfo?.rowData)
      break
    case ActionName.REMOVE:
      showRemoveBankdraftDialog((rowInfo?.rowData as any).id, (rowInfo?.rowData as any).description)
      break
  }
}

// WATCHERS
watchEffect(() => {
  if (propertyExist(registerFormData.value, 'B2BpaymentMetod') && isPaymentTypeWithBankdata.value) {
    const bankdataField: any = generatorFormSchema.value.find((item: any) => item.id === InvoicesFormFields.BANKDATA)
    if (bankdataField) {
      const bankdataFieldDisabled = !enablebankdataField(registerFormData.value.B2BpaymentMetod)
      bankdataField.disabled = bankdataFieldDisabled
      if (!bankdataFieldDisabled) {
        bankdataField.validationRules = 'required'
      }
      bankdataField.propItems = bankdata.value
    }
  }
})

watchEffect(() => {
  if (registerFormData.value && generatorFormSchema.value) {
    enableOrDisableGeneratorButton()
  }
})

// METHODS
function showRemoveBankdraftDialog(bankdraftId: number, bankdraftDescription: string) {
  getDialogInfo({
    message: t('components.dialog.remove_bankdraft', { bankdraftDescription }).toString(),
    action: () => doRemoveBankdraft(bankdraftId, bankdraftDescription),
    mainButtonText: texts.buttons.remove,
    secondaryButtonText: texts.buttons.cancel
  })
}

function showRemovePartialbankdraftDialog(partialbankdraft: Partialbankdraft) {
  getDialogInfo({
    message: t('components.dialog.remove_partialbankdraft', {
      partialbankdraftNotes: partialbankdraft.notes
    }).toString(),
    action: () => doRemovePartialbankdraft(partialbankdraft),
    mainButtonText: texts.buttons.remove,
    secondaryButtonText: texts.buttons.cancel
  })
}

async function generateAutomaticBankdraft() {
  const restore = {
    ...registerFormData.value
  }
  const formData = {
    ...registerFormData.value,
    [InvoicesFormFields.PAYMENT_TYPE]: registerFormData.value.idPaymentType?.id
  }
  await prepareFormData(formData)
  await saveCustomerInvoice(customerInvoiceId)
  await automaticInsert(customerInvoiceId)
  await prepareFormData(restore)
  if (!existsEndpointError.value) {
    await reloadData()
  }
}

function cloningSelectedRegisterCompanyInfo() {
  const clonedRegisterData = JSON.parse(JSON.stringify(customerInvoice))
  const { idCompany, exercise, sequence, number: invoiceNumber } = clonedRegisterData
  selectedInvoiceCompanyInfoCloned.value = {
    idCompany: String(idCompany),
    exercise,
    sequence,
    number: invoiceNumber
  }
}

function showBankdradftCreateForm() {
  bankdraftPromptTitle.value = texts.promptTitles.newBankdraft
  bankdraftFormData.value = {}
  bankdraftPromptOpened.value = true
}

async function showBankdradftEditForm(item: any) {
  bankdraftPromptTitle.value = texts.promptTitles.editBankdraft
  bankdraftFormData.value = await getBankdraft(item.id)
  validateSageExported(bankdraftFormData.value)
  bankdraftPromptOpened.value = true
}

function showPartialbankdraftCreateForm(bankdraft: any) {
  if (texts.charged === bankdraft.chargedText) {
    getDialogWarning({
      message: t('components.dialog.partialbankdraft_create_advise').toString()
    })
    return
  }
  bankdraftId.value = bankdraft.id
  partialbankdraftPromptTitle.value = texts.promptTitles.newPartialbankdraft
  partialbankdraftFormData.value = {}
  partialbankdraftPromptOpened.value = true
}

function showPartialbankdraftEditForm(item: any) {
  partialbankdraftPromptTitle.value = texts.promptTitles.editPartialbankdraft
  partialbankdraftFormData.value = item
  partialbankdraftPromptOpened.value = true
}

async function savePartialbankdraft() {
  partialbankdraftFormData.value.id
    ? saveExistPartialbankdraft(partialbankdraftFormData.value)
    : saveNewPartialbankdraft(partialbankdraftFormData.value, bankdraftId.value)
  if (!existsEndpointError.value) {
    await reloadData()
  }
}

async function doRemoveBankdraft(bankdraftId: number, bankdraftDescription: string) {
  await removeCustomerInvoiceBankdraft(bankdraftId, bankdraftDescription)
  if (!existsEndpointError.value) {
    bankdraftPromptOpened.value = false
    await reloadData()
  }
}

async function doRemovePartialbankdraft(partialbankdraft: Partialbankdraft) {
  await removePartialbankdraft(partialbankdraft)
  if (!existsEndpointError.value) {
    await reloadData()
  }
}

async function reloadData() {
  loading.value = true
  await loadCustomerInvoice(customerInvoiceId)
  await loadBankdraftList(customerInvoiceId)
  await loadTotals(customerInvoiceId)
  loading.value = false
  clearInvoiceCharged()
}

// HOOKS
onMounted(async () => {
  loading.value = true
  await loadCustomerInvoice(customerInvoiceId)
  await prepareFormData(customerInvoice)
  await loadTotals(customerInvoiceId)
  await loadGeneratorFormSchema()
  await loadBankdraftList(customerInvoiceId)
  await loadBankdraftFormSchema()
  await loadPartialbankdraftFormSchema()
  cloningSelectedRegisterCompanyInfo()

  // bankdata logic
  setPaymentTypeId(customerInvoice.B2BpaymentMetod)
  await loadBankdata()

  enableOrDisableGeneratorButton()
  loading.value = false
})

return { __sfc: true,emit, t, texts, loading, selectedInvoiceCompanyInfoCloned, bankdraftId, bankdraftFormData, partialbankdraftFormData, bankdraftPromptOpened, partialbankdraftPromptOpened, bankdraftPromptTitle, partialbankdraftPromptTitle, invoicesMaxNumberFormFieldsLogicMixin, billingMixin, prepareFormData, registerFormData, isValidFormData, getDialogInfo, getDialogWarning, breakpointName, customerInvoiceId, invoiceCode, totals, bankdraftList, existsEndpointError, customerInvoice, removeCustomerInvoiceBankdraft, removePartialbankdraft, saveNewPartialbankdraft, saveExistPartialbankdraft, saveCustomerInvoice, automaticInsert, loadCustomerInvoice, loadTotals, loadBankdraftList, getBankdraft, clearInvoiceCharged, generatorFormContext, generatorFormSchema, loadGeneratorFormSchema, bankdraftFormContext, bankdraftFormSchema, saveBankdraftForm, loadBankdraftFormSchema, isPaymentTypeWithBankdata, isDirectDebitWithoutBankdata, loadBankdata, bankdata, setPaymentTypeId, validateSageExported, partialbankdraftFormContext, partialbankdraftFormSchema, loadPartialbankdraftFormSchema, viewPermission, permissionsEntity, canSave, canDelete, gridConfiguration, getPartialBankdraftList, miniGrid, onRowDataBound, breakpointClass, toolbarOptionsCustom, contextMenuItems, alert, onIsValidForm, onAutomaticBankdraft, onChangeGeneratorForm, enablebankdataField, enableOrDisableGeneratorButton, onChangeBankdraftForm, resetBankdraftFormSchema, onBankdraftPromptExecuteAction, onPartialbankdraftPromptExecuteAction, onCommandClick, onToolbarClicked, onRowClicked, onContextMenuClicked, showRemoveBankdraftDialog, showRemovePartialbankdraftDialog, generateAutomaticBankdraft, cloningSelectedRegisterCompanyInfo, showBankdradftCreateForm, showBankdradftEditForm, showPartialbankdraftCreateForm, showPartialbankdraftEditForm, savePartialbankdraft, doRemoveBankdraft, doRemovePartialbankdraft, reloadData, SpinnerLayerComponent, FormGeneratorComponent, LfGridComponent, PromptDialogComponent, LfTotalsComponent, StaticAlertComponent }
}

})